/**
 * Importar un cuestionario.
 *
 * Tres vías de entrada y **ninguna exige ratón**: pegar el texto, elegir un archivo con el
 * selector de siempre, o arrastrarlo. Arrastrar es cómodo pero no es accesible por sí solo, así
 * que nunca es la única forma (principio IV).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { Aviso } from '../components/sistema/aviso.js';
import { Boton } from '../components/sistema/boton.js';
import { Marco } from '../components/sistema/marco.js';
import { Rotulo } from '../components/sistema/rotulo.js';
import { parseExchangeFile, type ImportError, type QuizDraft } from '@clica/shared';
import type { SessionProfile } from '@clica/shared';
import { importQuiz } from '../content/import.js';
import { FichaOpcion } from '../components/ficha-opcion.js';

/** Glifo decorativo: en constante porque la regla de i18n no distingue un símbolo de una frase. */
const OK_GLYPH = '✓';

type Estado =
  | { readonly kind: 'idle' }
  | { readonly kind: 'errors'; readonly errors: readonly ImportError[] }
  | { readonly kind: 'preview'; readonly draft: QuizDraft }
  | { readonly kind: 'writing' }
  | { readonly kind: 'writeFailed' };

function InformeErrores({ errors }: { errors: readonly ImportError[] }): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <section aria-labelledby="titulo-errores" className="border-ko mt-6 rounded-lg border-2 p-4">
      {/* La región tiene nombre para poder saltar a ella; el anuncio va en un elemento aparte.
          Poner `role="status"` en la propia sección pisaría su rol de región y dejaría al lector
          de pantalla sin forma de navegar hasta aquí. */}
      <h2 id="titulo-errores" className="font-semibold">
        <span aria-hidden="true">⚠ </span>
        {t('import.errorsTitle')}
      </h2>
      <p className="mt-1 text-sm">{t('import.errorsLead', { count: errors.length })}</p>

      {/* Con cortesía: aparece tras una acción del profesor y no interrumpe nada que se escriba. */}
      <p role="status" aria-live="polite" className="sr-only">
        {t('import.errorsTitle')}. {t('import.errorsLead', { count: errors.length })}
      </p>

      <ul className="mt-3 flex flex-col gap-2 text-sm">
        {errors.map((error, index) => (
          <li key={index} className="flex flex-wrap gap-2">
            <span className="text-text-dim font-mono">
              {error.questionNumber === undefined
                ? t('import.errorInFile')
                : t('import.errorAt', {
                    number: error.questionNumber,
                    field: error.field ?? '',
                  })}
            </span>
            <span>{t(`importError.${error.reason}`, { detail: error.detail ?? '' })}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function VistaPreviaImportacion({ draft }: { draft: QuizDraft }): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <section aria-labelledby="titulo-previa" className="border-line mt-6 rounded-lg border p-4">
      <h2 id="titulo-previa" className="font-semibold">
        {t('import.previewTitle')}
      </h2>
      <p className="text-text-dim mt-1 text-sm">{t('import.previewLead')}</p>

      <p className="mt-3 text-lg font-semibold">{draft.title}</p>

      <ol className="mt-3 flex flex-col gap-3">
        {draft.questions.map((question, index) => (
          <li key={index} className="border-line rounded border p-3">
            <p className="text-text-dim text-xs">
              {t('import.questionNumber', { number: index + 1 })}
            </p>
            {/* El enunciado se muestra como TEXTO PLANO, no renderizado: lo escribe un modelo de
                lenguaje y aquí solo hace falta reconocerlo, no verlo bonito. */}
            <p className="mt-1 line-clamp-3 text-sm">{question.prompt_md}</p>
            <ul className="mt-2 flex flex-wrap gap-3">
              {question.options.map((option, position) => (
                <li key={position} className="flex items-center gap-1.5 text-sm">
                  <FichaOpcion position={position} size={24} />
                  <span>{option.text_md}</span>
                  {option.is_correct && (
                    <>
                      <span aria-hidden="true">{OK_GLYPH}</span>
                      <span className="sr-only">{t('import.correct')}</span>
                    </>
                  )}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ol>
    </section>
  );
}

export function PantallaImportar({ profile }: { profile: SessionProfile }): React.JSX.Element {
  const { t } = useTranslation();
  const { subjectId = '' } = useParams();
  const navigate = useNavigate();

  const [text, setText] = useState('');
  const [estado, setEstado] = useState<Estado>({ kind: 'idle' });
  const [dragging, setDragging] = useState(false);

  function analyse(source: string): void {
    setText(source);
    const result = parseExchangeFile(source);
    setEstado(
      result.ok
        ? { kind: 'preview', draft: result.draft }
        : { kind: 'errors', errors: result.errors },
    );
  }

  /** Lo que un archivo de intercambio puede llegar a pesar. Más que eso no es uno de los nuestros. */
  const MAX_BYTES = 2 * 1024 * 1024;

  async function readFile(fileList: FileList | null): Promise<void> {
    const file = fileList?.[0];
    if (file === undefined) return;

    // Antes se leía cualquier cosa que se soltara encima. El `accept` del selector no vincula al
    // arrastre, así que un vídeo de doscientos megas se leía entero en memoria antes de darse
    // cuenta de que no era JSON.
    if (file.size > MAX_BYTES) {
      setEstado({ kind: 'errors', errors: [{ reason: 'tooLarge', detail: String(MAX_BYTES) }] });
      return;
    }

    analyse(await file.text());
  }

  async function confirm(draft: QuizDraft): Promise<void> {
    setEstado({ kind: 'writing' });
    const outcome = await importQuiz(profile.id, subjectId, draft);

    if (!outcome.ok) {
      setEstado({ kind: 'writeFailed' });
      return;
    }
    await navigate(`/cuestionarios/${outcome.quiz.id}`);
  }

  return (
    <Marco
      profile={profile}
      migas={[{ texto: t('subjects.title'), a: '/asignaturas' }, { texto: t('import.title') }]}
    >
      <main id="contenido" className="mx-auto max-w-3xl p-6">
        <p className="mb-4">
          <Link
            to={`/asignaturas/${subjectId}`}
            className="rounded-chip inline-flex min-h-11 items-center underline underline-offset-4"
          >
            {t('quizzes.title')}
          </Link>
        </p>

        <h1 className="font-display text-h1 font-extrabold text-balance">{t('import.title')}</h1>
        <p className="text-lead text-text-dim mt-2">{t('import.lead')}</p>

        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            void readFile(e.dataTransfer.files);
          }}
          // Arrastrando: cambia el borde, el fondo Y aparece un rótulo. Solo el color incumple la
          // regla de que nada se comunica únicamente por color.
          className={[
            'rounded-card mt-6 border-2 border-dashed p-4 transition-colors duration-(--dur-state)',
            dragging ? 'border-edge bg-surface-hi' : 'border-line',
          ].join(' ')}
        >
          {dragging && (
            <p className="text-micro font-display text-text mb-2 font-bold uppercase">
              {t('import.dropHere')}
            </p>
          )}
          <Rotulo htmlFor="pegar-json" className="mb-1 block">
            {t('import.paste')}
          </Rotulo>
          <textarea
            id="pegar-json"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={8}
            placeholder={t('import.placeholder')}
            className="text-body rounded-chip border-line bg-surface w-full border-2 px-3 py-2.5 font-mono"
          />

          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Boton variante="primaria" onClick={() => analyse(text)} disabled={text.trim() === ''}>
              {t('import.analyse')}
            </Boton>

            <Rotulo>{t('import.orDrop')}</Rotulo>

            {/*
            El selector de archivo de siempre: es la alternativa accesible al arrastre.

            El `input` va oculto con `sr-only` —un rectángulo de 1x1 px recortado—, así que el
            anillo de foco caía ahí y quien navega con teclado no veía dónde estaba. Con
            `focus-within` el anillo lo pinta la etiqueta, que es lo que se ve.
          */}
            <label className="border-line rounded-chip text-body focus-within:outline-focus-out flex min-h-11 cursor-pointer items-center border-2 px-4 focus-within:outline-3 focus-within:outline-offset-3">
              {t('import.chooseFile')}
              <input
                type="file"
                accept=".json,application/json"
                onChange={(e) => void readFile(e.target.files)}
                className="sr-only"
              />
            </label>
          </div>
        </div>

        {estado.kind === 'errors' && <InformeErrores errors={estado.errors} />}

        {estado.kind === 'writeFailed' && (
          <Aviso tono="error" interrumpe className="mt-6">
            {t('import.writeFailed')}
          </Aviso>
        )}

        {estado.kind === 'preview' && (
          <>
            <VistaPreviaImportacion draft={estado.draft} />
            <div className="mt-4 flex flex-wrap gap-3">
              <Boton variante="primaria" onClick={() => void confirm(estado.draft)}>
                {t('import.confirm', { count: estado.draft.questions.length })}
              </Boton>
              <button
                type="button"
                onClick={() => setEstado({ kind: 'idle' })}
                className="border-line rounded border px-4 py-2"
              >
                {t('import.cancel')}
              </button>
            </div>
          </>
        )}

        {estado.kind === 'writing' && (
          <p role="status" className="mt-6">
            {t('import.importing')}
          </p>
        )}
      </main>
    </Marco>
  );
}
